import { DateTime } from 'luxon';
import { useMemo } from 'react';

import { useDataProvider } from '@work4all/data';

import { W4ADateFormat } from '@work4all/models/lib/additionalEnums/DateFormat.enum';
import { User } from '@work4all/models/lib/Classes/User.entity';
import { Vacation } from '@work4all/models/lib/Classes/Vacation.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

export interface VacationStats {
  year: number;
  base: number;
  carriedOver: number;
  total: number;
  used: number;
  remaining: number;
}

export function useVacationStats({
  userId,
  year,
}: {
  userId: number;
  year: number;
}) {
  const requestVacationEntitlement = useMemo(() => {
    const data: User = {
      id: null,
      vactationEntitlement: [
        {
          id: null,
          jahr: null,
          anspruch: null,
          vorjahr: null,
          bisJetzt: null,
        },
      ],
    };

    const request: DataRequest = {
      operationName: 'GetUserVacationEntitlement',
      entity: Entities.user,
      data,
      filter: [{ id: { $eq: userId } }],
    };

    return request;
  }, [userId]);

  const responseVacationEntitlement = useDataProvider<User>(
    requestVacationEntitlement
  );

  const requestVacationTaken = useMemo(() => {
    const data: Vacation = {
      id: null,
      amount: null,
      simpleVacationKind: {
        id: null,
      },
    };

    const request: DataRequest = {
      entity: Entities.vacation,
      data,
      filter: [
        { userId: { $eq: userId } },
        {
          date: {
            $gt: DateTime.now()
              .set({ year })
              .startOf('year')
              .toFormat(W4ADateFormat.DEFAULT),
          },
        },
        {
          date: {
            $lt: DateTime.now()
              .set({ year })
              .endOf('year')
              .toFormat(W4ADateFormat.DEFAULT),
          },
        },
      ],
    };

    return request;
  }, [userId, year]);

  const responseVacationTaken = useDataProvider<Vacation>(requestVacationTaken);
  const vacationTaken = responseVacationTaken.data.filter(
    (vacation) => vacation.simpleVacationKind === null
  );
  const refetch = responseVacationEntitlement.refetch;

  const stats = useMemo<VacationStats>(() => {
    const found =
      responseVacationEntitlement.data[0]?.vactationEntitlement?.find(
        ({ jahr }) => jahr === year
      );

    const base = found?.anspruch ?? 0;
    const carriedOver = found?.vorjahr ?? 0;
    const used = vacationTaken.length
      ? vacationTaken?.map((x) => x.amount)?.reduce((a, b) => a + b)
      : 0;

    const total = base + carriedOver;
    const remaining = total - used;

    return {
      year,
      base,
      carriedOver,
      total,
      used,
      remaining,
    };
  }, [responseVacationEntitlement.data, vacationTaken, year]);

  return {
    stats,
    refetch,
    isLoading: responseVacationTaken.loading,
  };
}
